import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import {
  LayoutDashboard,
  Package,
  ScanLine,
  ClipboardList,
  Receipt,
  LogOut,
  Sparkles,
  Menu,
  X,
  ChevronDown,
  MoreHorizontal,
  Truck,
  ArrowLeftRight,
  GitBranch,
  History,
  CalendarClock,
  Gauge,
  HardHat,
  Activity,
  Settings as SettingsIcon,
  Calculator as TallyIcon,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import BrandMark from './BrandMark';
import { PAGE_CATALOG, currentProduct, keyMatchesProduct, RESTRICTED_PAGE_KEYS } from '../lib/pageCatalog';
import { isTallyAllowed } from '../lib/tallyAllowlist';
import { isApiKeyAdmin } from '../lib/apiKeyAllowlist';
import { MasterSearch } from './MasterSearch';

/**
 * Sidebar philosophy: 5 top-level items that cover 90% of daily use, with
 * one collapsible "More" group for everything else (procurement chain, masters,
 * admin tools). v1's seven sub-grouped menu sprawl was the pain point.
 */

// "Spare Part Requisition" is the canonical first step — every spare flow
// starts here (pick a JO → in-stock auto-issues, shortfalls auto-PO). The
// `mystical: true` flag promotes it above all other items and renders with
// a shimmering gradient + pulsing ring so users never start at Scan IN/OUT
// or Issue Notes by mistake. Points at /indents because that's the page
// rendering the From System / From Legacy requisition list (the one in
// the user's screenshot — not the older /requisitions page).
//
// `pageKey` ties each row to the shared page catalog so the per-user
// allowedPages list filters the sidebar without duplicating route names.
type PrimaryItem = {
  to: string;
  label: string;
  icon: any;
  end?: boolean;
  highlight?: boolean;
  mystical?: boolean;
  pageKey: string;
};
const primary: PrimaryItem[] = [
  { to: '/indents', label: 'Spare Part Requisition', icon: ClipboardList, mystical: true, pageKey: 'indents' },
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, pageKey: 'dashboard' },
  { to: '/scan', label: 'Scan IN/OUT', icon: ScanLine, highlight: true, pageKey: 'scan' },
  { to: '/jo-tracker', label: 'Job Order Tracker', icon: GitBranch, pageKey: 'jo-tracker' },
  { to: '/container-service-history', label: 'Container History', icon: History, pageKey: 'container-service-history' },
  { to: '/pm-schedule', label: 'PM Schedule', icon: CalendarClock, pageKey: 'pm-schedule' },
  // Needs Confirmation is no longer a standalone sidebar row — it now lives as a
  // sub-tab INSIDE Reports & Analytics → Cost Report (same money: the queue
  // confirms the very rates that report estimates). Route + pageKey are kept so
  // deep links still resolve; re-adding a row here is all it takes to restore.
  // Monday Meeting temporarily pulled from the sidebar — its data is known
  // wrong (flagged 2026-08-27). Route/pageKey/permissions untouched, so it's
  // still reachable by direct URL and re-adding this row is all it takes.
  // { to: '/service-monday-report', label: 'Monday Meeting', icon: CalendarDays, pageKey: 'service-monday-report' },
  { to: '/live-status', label: 'Live Status', icon: Activity, highlight: true, pageKey: 'live-status' },
  // Efficiency Intelligence — Service and Purchase, each scored on its own TAT
  // mapping. One page carries both; the two scores are never combined.
  { to: '/efficiency', label: 'Efficiency', icon: Gauge, pageKey: 'efficiency' },
  { to: '/technicians', label: 'Technicians', icon: HardHat, pageKey: 'technicians' },
  { to: '/deliveries', label: 'Deliveries', icon: Truck, pageKey: 'deliveries' },
  { to: '/partcodes', label: 'Part Codes', icon: Package, pageKey: 'partcodes' },
  { to: '/transactions', label: 'Transactions', icon: ArrowLeftRight, pageKey: 'transactions' },
  { to: '/bills', label: 'Bills', icon: Receipt, pageKey: 'bills' },
];

// Everything the user *can* reach, but rarely needs in-hand. Flat list —
// no sub-sub-groups. Sorted by how often a typical user touches them.
//
// Consumable Containers routes still exist for direct-URL access but are
// intentionally hidden from the sidebar — not part of the daily flow yet.
// (Petty Cash is now a first-class section — listed below.)
// Each entry is one row. `matches` lets a single button stay highlighted
// across multiple related routes (e.g. Masters & SKU Requests button is
// active on both /masters and /partcode-requests). The page itself shows
// an in-page tab to flip between the two — sidebar stays clean.
// `anyPageKey` is for rows that front a hub of several catalog pages: the row
// shows when the user holds ANY one of the listed keys (the hub then hides the
// tabs they aren't granted). `pageKey` stays the row's primary key.
// `adminOnly` rows never render for a non-admin, whatever their allowedPages
// says — used by the API-key admin, which the backend also gates on the admin
// role (requireRole('admin') in api-keys.routes.ts).
// `ownerOnly` is stricter than `adminOnly`: the row renders only for the named
// API owner (see lib/apiKeyAllowlist). An API key reads the whole company, so
// minting one is not a capability every admin should have.
type SecondaryItem = { to: string; label: string; matches?: string[]; pageKey: string; anyPageKey?: string[]; adminOnly?: boolean; ownerOnly?: boolean };
const secondary: SecondaryItem[] = [
  // /indents is now promoted to the mystical-glow row in `primary` above —
  // duplicating it here would just add noise. Material Requisition still
  // redirects to /indents (App.tsx) so old bookmarks keep working.
  // Procurement-flow trio: PO → GRN → Issue Note. One sidebar row,
  // in-page tabs at the top let the user flip between them.
  { to: '/pos', label: 'POs / GRNs / Issues / On-Site', matches: ['/pos', '/grns', '/issue-notes', '/onsite'], pageKey: 'procurement' },
  { to: '/accounts/purchases', label: 'Accounts (Purchases)', matches: ['/accounts'], pageKey: 'accounts' },
  { to: '/petty-cash', label: 'Petty Cash', pageKey: 'petty-cash' },
  { to: '/vendors', label: 'Vendors', pageKey: 'vendors' },
  { to: '/vendor-coord', label: 'Vendor Coordination', pageKey: 'vendor-coord' },
  { to: '/travel-planning', label: 'Travel Planning', pageKey: 'travel-planning' },
  { to: '/service-requests', label: 'Service Requests', pageKey: 'service-requests' },
  // ONE button each — the destination page renders an in-page tab to flip
  // to the sibling. Sidebar stays compact, no nested rows.
  { to: '/masters', label: 'Masters & SKU Requests', matches: ['/masters', '/partcode-requests'], pageKey: 'masters' },
  { to: '/approval-rules', label: 'Approval Rules & Audit', matches: ['/approval-rules', '/audit'], pageKey: 'approval-rules' },
  // Reports, Service Report, Service Analytics and Recurring/Chargeable are one
  // hub page now (/reports, two levels of in-page tabs). The row shows if the
  // user holds ANY of the four keys; the hub hides the tabs they don't hold.
  {
    to: '/reports',
    label: 'Reports & Analytics',
    matches: ['/reports', '/service-report', '/analytics/service', '/recurring-chargeable'],
    pageKey: 'reports',
    anyPageKey: ['reports', 'service-report', 'service-analytics', 'recurring-chargeable'],
  },
  // Crystal Public API v1. The Integration Kit is the developer doc (anyone
  // granted the page); API Keys mints/revokes/rotates keys and is admin-only.
  { to: '/integration-kit', label: 'Integration Kit', pageKey: 'integration-kit' },
  { to: '/api-keys', label: 'API Keys', pageKey: 'api-keys', adminOnly: true, ownerOnly: true },
  // IMS Containers is intentionally NOT listed here. It's a separate product
  // surface — accessed via its own login (or direct URL). Keeping it out of
  // the Spares sidebar so users can't accidentally cross over.
];

/**
 * Filter sidebar items by per-user access. Admins see everything; for other
 * roles we use the user's allowedPages catalog keys, falling back to "show
 * all" when the field is absent (legacy users on existing accounts).
 */
function isPageAllowed(pageKey: string, role: string, allowedPages?: string[] | null): boolean {
  if (role === 'admin') return true;
  // A RESTRICTED page is never covered by the legacy "show all" fallback —
  // otherwise adding one to the catalog silently reveals it to every account
  // that predates per-page grants. It needs the key, explicitly.
  if (RESTRICTED_PAGE_KEYS.includes(pageKey)) {
    return Array.isArray(allowedPages) && allowedPages.includes(pageKey);
  }
  // Legacy users with no explicit list keep full access — back-compat.
  if (allowedPages == null) return true;
  return allowedPages.includes(pageKey);
}
void PAGE_CATALOG; // keep import alive for type harmony with /settings UI

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Domain-scoped product view (spares. vs service.). null = dev/unscoped =
  // show everything. IMS runs under its own layout, so here it's only ever
  // 'spares' | 'service' | null.
  const product = useMemo(() => currentProduct(), []);
  const brandName = product === 'service' ? 'Service Desk' : 'Spares IMS';

  // Per-user filtered nav. Admin sees everything (subject to the product view);
  // others see only their allowedPages entries (back-compat: legacy null = full).
  const visiblePrimary = useMemo(
    () => primary
      .filter((p) => keyMatchesProduct(p.pageKey, product))
      .filter((p) => isPageAllowed(p.pageKey, user?.role || '', user?.allowedPages)),
    [user?.role, user?.allowedPages, product],
  );
  const visibleSecondary = useMemo(
    () => secondary
      .filter((s) => !s.adminOnly || user?.role === 'admin')
      .filter((s) => !s.ownerOnly || isApiKeyAdmin(user?.email))
      .filter((s) => (s.anyPageKey ?? [s.pageKey]).some((k) => keyMatchesProduct(k, product)))
      .filter((s) => (s.anyPageKey ?? [s.pageKey])
        .some((k) => isPageAllowed(k, user?.role || '', user?.allowedPages))),
    [user?.role, user?.allowedPages, product],
  );

  // Pending travel approvals awaiting THIS user (Sitaram / Pushpa) — drives the
  // red badge on the "Travel Planning" nav item. Shares the dashboard cache.
  const { data: pending } = useQuery<{ cards?: { travelApproval?: { count?: number } } }>({
    queryKey: ['my-pending'],
    queryFn: () => api.get('/api/dashboard/my-pending'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const travelPendingCount = pending?.cards?.travelApproval?.count || 0;

  // Auto-open "More" if the current route lives there, so the sidebar
  // always shows where you are.
  const onSecondaryRoute = useMemo(
    () => visibleSecondary.some((s) => {
      const all = s.matches ?? [s.to];
      return all.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
    }),
    [location.pathname, visibleSecondary]
  );
  const [moreOpen, setMoreOpen] = useState(onSecondaryRoute);
  useEffect(() => { if (onSecondaryRoute) setMoreOpen(true); }, [onSecondaryRoute]);

  // Close drawer on route change + lock body scroll while open
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div className="min-h-screen md:flex">
      {/* Mobile top bar — visible only < md */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-slate-100"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <BrandMark size={26} />
          <span className="font-display text-sm font-extrabold">{brandName}</span>
        </div>
        <div className="w-8" />
      </header>

      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-slate-200 bg-white',
          // Mobile: slide-in drawer.
          'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
          // Desktop: STICKY AND IN FLOW — deliberately not `fixed`.
          //
          // A fixed rail is out of the document flow, so <main> has to be pushed
          // clear of it with a matching margin. Those two facts live in different
          // elements, and if they ever disagree the sidebar lands on top of the
          // content. An in-flow sticky rail physically occupies its 240px, so
          // overlap is not merely fixed — it's impossible. No margin to keep in
          // sync, nothing to drift.
          //
          // (`sticky` only became safe once html/body moved from `overflow-x:
          // hidden` to `clip` — `hidden` makes a scroll container, which silently
          // stops sticky from sticking. See index.css.)
          //
          // `h-screen` (not `min-h-screen`) caps it at one viewport, so the nav
          // scrolls inside the rail instead of the rail growing with the page.
          'md:sticky md:top-0 md:h-screen md:w-60 md:max-w-none md:translate-x-0',
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <BrandMark size={28} />
            <div>
              <div className="font-display text-sm font-extrabold">{brandName}</div>
              <div className="text-xs text-slate-500">Crystal Logistic</div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* min-h-0 is what lets this actually shrink inside the flex column —
            without it the nav refuses to scroll and pushes the footer off-screen. */}
        <nav className="min-h-0 flex-1 overflow-y-auto p-2">
          {/* Primary — filtered by allowedPages */}
          <div className="space-y-1">
            {visiblePrimary.map(({ to, label, icon: Icon, end, highlight, mystical }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    // DS §Navigation/.navitem — one nav shape for every realm.
                    'navitem',
                    !mystical && isActive && 'on',
                    highlight && !isActive && !mystical && 'border border-dashed border-navy-300',
                    mystical && 'mystical-glow'
                  )
                }
              >
                <Icon size={18} />
                <span>{label}</span>
                {highlight && !mystical && <Sparkles size={14} className="ml-auto text-brand-500" />}
                {mystical && <Sparkles size={16} className="ml-auto mystical-sparkle text-white" />}
              </NavLink>
            ))}
          </div>

          {/* More — collapsible */}
          <div className="mt-3">
            <button
              onClick={() => setMoreOpen((v) => !v)}
              className="navitem"
              aria-expanded={moreOpen}
            >
              <MoreHorizontal size={18} />
              <span>More</span>
              <ChevronDown size={16} className={cn('ml-auto transition-transform', moreOpen && 'rotate-180')} />
            </button>
            {moreOpen && (
              <div className="mt-1 space-y-0.5 pl-2 border-l border-slate-200 ml-4">
                {visibleSecondary.map(({ to, label, matches }) => {
                  const all = matches ?? [to];
                  const active = all.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className={cn('navitem py-2 text-[12px]', active && 'on')}
                    >
                      <span>{label}</span>
                      {to === '/travel-planning' && travelPendingCount > 0 && (
                        <span className="tally"
                          title={`${travelPendingCount} travel approval${travelPendingCount === 1 ? '' : 's'} awaiting your sign-off`}>
                          {travelPendingCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        {/* shrink-0 pins the user/logout block to the bottom of the rail — a long
            nav scrolls behind it instead of pushing it off-screen. */}
        <div className="shrink-0 border-t border-slate-200 p-4">
          <div className="text-xs text-slate-500">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
          {(user?.role === 'admin' || (user?.email || '').toLowerCase() === 'service@crystalgroup.in') && (
            <>
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn('navitem mt-3 py-2 text-[12px]', isActive && 'on')
                }
              >
                <SettingsIcon size={14} /> Settings
              </NavLink>
              {/* Tally Sync — restricted to a hardcoded allowlist on the FE
                  (cosmetic gate) AND a server-side email check on every
                  /api/tally/* request (the real security). Both lists are
                  the same; backend wins on disagreement. */}
              {isTallyAllowed(user?.email) && (
                <NavLink
                  to="/tally-sync"
                  className={({ isActive }) =>
                    cn(
                      'mt-1 flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors',
                      isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100'
                    )
                  }
                >
                  <TallyIcon size={14} /> Tally Sync
                </NavLink>
              )}
            </>
          )}
          <button onClick={logout} className="btn-ghost mt-2 w-full text-xs">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* The shell used to cap every page at max-w-6xl, which is what made wide
          screens look half-empty while tables side-scrolled inside that column.
          The cap is now the same 1800px the `.page` class uses, so a converted
          page and a not-yet-converted one line up identically. */}
      {/* No left margin: on desktop the rail is IN the flex flow and takes its own
          240px, so content simply starts after it. On mobile it's an overlay
          drawer and main gets the full width. */}
      <main className="w-full min-w-0 flex-1 bg-slate-50 p-3 sm:p-4 lg:p-6">
        <div className="mx-auto w-full max-w-[1800px]">
          <Outlet />
        </div>
      </main>
      <MasterSearch />
    </div>
  );
}
